import { JSX, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  ScrollRestoration,
} from 'react-router-dom';

import { faRoadBarrier } from '@fortawesome/pro-solid-svg-icons';
import { RoleCode, roleCodeName } from 'corso-types';
import ClaimsLayout from './features/claimReview/ClaimsLayout';
import AuxiliaryLayout from './layouts/AuxiliaryLayout';
import SignUpLoader from './loaders/SignUpLoader';

import { LoadingProvider, PageTitleProvider } from 'corso-admin-ui';
import { CorsoAiChatContextProvider } from './components/ai/CorsoAiChat';
import PageStatus from './components/PageStatus';
import useIsCorsoAdmin from './hooks/useIsCorsoAdmin';
import CXReportPage from './features/analytics/CXReportPage';
import { DashboardPage } from './features/analytics/DashboardPage';
import { HomePage } from './features/analytics/home/HomePage';
import InvoicesPage from './features/billing/InvoicesPage';
import ClaimReviewPage from './features/claimReview/ClaimReviewPage';
import ClaimsTablePage from './features/claimReview/ClaimsTablePage';
import OrderLookupPage from './features/orderLookup/OrderLookupPage';
import ProductDetailPage from './features/products/ProductDetailPage';
import ProductsLayout from './features/products/ProductsLayout';
import ProductsTablePage from './features/products/ProductsTablePage';
import RegistrationReviewPage from './features/registrationReview/RegistrationReviewPage';
import RegistrationsLayout from './features/registrationReview/RegistrationsLayout';
import RegistrationsTablePage from './features/registrationReview/RegistrationsTablePage';
import SettingsLayout, {
  queryParamKey,
} from './features/settings/SettingsLayout';
import { SettingsPage } from './features/settings/SettingsPage';
import ShippingClaimReview from './features/shippingClaimReview/ShippingClaimReviewPage';
import ShippingClaimsLayout from './features/shippingClaimReview/ShippingClaimsLayout';
import ShippingClaimsTablePage from './features/shippingClaimReview/ShippingClaimsTablePage';
import WidgetIntegration from './integrations/WidgetIntegration';
import AppLayout from './layouts/AppLayout';
import UserWelcomeLoader from './loaders/UserWelcomeLoader';
import DefaultStoreNavigation from './pages/DefaultStoreNavigation';
import CustomerInsight from './pages/integrations/CustomerInsight';
import DefaultIntegrationNavigation from './pages/integrations/DefaultIntegrationNavigation';
import NotFound from './pages/NotFound';
import SignOut from './pages/SignOut';
import SignUp from './pages/SignUp';
import UnhandledError from './pages/UnhandledError';
import UserWelcome from './pages/UserWelcome';
import AutoAuthProvider from './providers/AutoAuthProvider';
import InitialLocationProvider from './providers/InitialLocation';
import StickySearchParamsProvider from './providers/StickySearchParams';
import {
  DefaultStoreRedirect,
  StoreUserProvider,
  useStoreUser,
} from './providers/StoreUserProvider';

/**
 * Given a `predicate`, if it is `false`, render the `denied` element; otherwise, render the `Outlet` to delegate to further routes.
 */
function RouteProtector({
  predicate,
  denied,
}: {
  /**
   * The condition to check for access, if `false`, the `denied` element will be rendered.
   */
  predicate: () => boolean;
  denied: JSX.Element;
}) {
  if (!predicate()) return denied;
  return <Outlet />;
}

/**
 * Protects a route given the `allowedRoles`, if the user's role is not in the list, they will be shown a message; otherwise, render the `Outlet` to delegate to further routes.
 */
function RoleProtector({
  allowedRoles,
}: {
  allowedRoles: `${Exclude<RoleCode, typeof RoleCode.corsoAdmin>}`[];
}) {
  const storeUser = useStoreUser();
  const { roleCode } = storeUser;
  return (
    <RouteProtector
      predicate={() =>
        roleCode === RoleCode.corsoAdmin || allowedRoles.includes(roleCode)
      }
      denied={
        <PageStatus
          title="You Don't Have Access"
          icon={faRoadBarrier}
          detail={`${
            allowedRoles.length === 1
              ? 'The following role is required to access this page:'
              : 'One of the following roles is required to access this page:'
          } ${allowedRoles.map((allowedRole) => roleCodeName[allowedRole]).join(', ')}.`}
        />
      }
    />
  );
}

/** Route which is protected for admins only. */
function AdminProtector() {
  return <RoleProtector allowedRoles={[RoleCode.admin]} />;
}

/** Route which only Corso admins can reach. */
function CorsoAdminProtector() {
  const isCorsoAdmin = useIsCorsoAdmin();
  return (
    <RouteProtector
      predicate={() => isCorsoAdmin}
      denied={
        <PageStatus
          title="Unauthorized"
          icon={faRoadBarrier}
          detail="You don't have access to this page."
        />
      }
    />
  );
}
/**
 * Mark the `root` element ready for rendering.
 * Allowing the page to show the content instead of the baked initial loader.
 */
function MarkReady() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) {
      console.error('#root Element Missing');
      return;
    }

    root.dataset.ready = 'ready';
  }, []);

  return null;
}

// ! verify src/pages/DefaultStoreNavigation.tsx routes when making changes to this file

// TODO add error boundaries to help with error handling, especially when a new version is deployed
const routes = createRoutesFromElements(
  <Route
    path="/"
    errorElement={<UnhandledError />}
    element={
      <InitialLocationProvider>
        <StickySearchParamsProvider>
          <WidgetIntegration />
          <MarkReady />
          {/* // TODO review scroll restoration with hashes */}
          <ScrollRestoration />
          <Outlet />
        </StickySearchParamsProvider>
      </InitialLocationProvider>
    }
  >
    <Route
      element={
        <AutoAuthProvider>
          <Outlet />
        </AutoAuthProvider>
      }
    >
      <Route path="sign-out" element={<SignOut />} />
      <Route index element={<DefaultStoreRedirect />} />
      <Route
        path=":storeId"
        element={
          <LoadingProvider>
            <PageTitleProvider>
              <StoreUserProvider>
                <CorsoAiChatContextProvider>
                  <AppLayout />
                </CorsoAiChatContextProvider>
              </StoreUserProvider>
            </PageTitleProvider>
          </LoadingProvider>
        }
      >
        {/* // ? is there some way to name/identify a route so that links can always go to it; i.e. making this a home without needing to always specify the `storeId`, maybe instead `navigate` with a basename */}
        <Route index element={<DefaultStoreNavigation />} />
        {/* // ? typically for embedded views */}
        <Route path="integrations" element={<Outlet />}>
          <Route index element={<DefaultIntegrationNavigation />} />
          {/* // TODO someday have customer insights a first class top-level */}
          <Route path="customer" element={<CustomerInsight />} />
          {/* // TODO update Kustomers integration configuration to just point to root route with ref and auth info, then this redirect can be dropped */}
          {/* // redirect old Kustomers integration into standard default integration navigation */}
          <Route
            path="customers"
            element={
              <Navigate
                to={{
                  pathname: '../customer',
                  search: window.location.search, // keep search
                }}
                relative="path"
                replace
              />
            }
          />
        </Route>

        {/* // * currently omitting dashboard at `/` until we know better what we want to display to merchants */}
        <Route path="registrations" element={<RegistrationsLayout />}>
          <Route index element={<RegistrationsTablePage />} />
          <Route path=":registrationId" element={<RegistrationReviewPage />} />
        </Route>

        <Route path="products" element={<ProductsLayout />}>
          <Route index element={<ProductsTablePage />} />
          <Route path=":storeProductId" element={<ProductDetailPage />} />
        </Route>

        <Route path="orders/lookup" element={<OrderLookupPage />}>
          <Route path=":orderIdFromPlatform?" element={<OrderLookupPage />} />
        </Route>

        <Route path="claims">
          {/* // * redirect to default route if no claim type selected */}
          <Route index element={<Navigate to=".." relative="path" replace />} />

          {/* redirect adding to facilitate the move to orders/lookup can be removed at some future date */}
          <Route
            path="create"
            element={<Navigate to="../../orders/lookup" replace />}
          />

          <Route path="shipping" element={<ShippingClaimsLayout />}>
            <Route index element={<ShippingClaimsTablePage />} />
            <Route path=":shippingClaimId" element={<ShippingClaimReview />} />
          </Route>

          <Route path=":claimType" element={<ClaimsLayout />}>
            <Route index element={<ClaimsTablePage />} />
            <Route path=":claimId" element={<ClaimReviewPage />} />
          </Route>
        </Route>

        <Route path="analytics" element={<AdminProtector />}>
          <Route index element={<Navigate to="dashboards" replace />} />
          <Route path="dashboards">
            <Route element={<CorsoAdminProtector />}>
              <Route path="home" element={<HomePage />} />
            </Route>
            <Route path=":group?" element={<DashboardPage />} />
          </Route>
          {/* TODO remove once bookmarks to the Corso-admin preview have aged out */}
          <Route
            path="dashboards-v2"
            element={<Navigate to="../dashboards" replace />}
          />
          {/* TODO remove once bookmarks to the Superset organization dashboards have aged out; the View select picks the organization now */}
          <Route
            path="organization/dashboards"
            element={<Navigate to="../dashboards" replace />}
          />

          <Route path="customer-success" element={<CXReportPage />} />
        </Route>
        <Route path="billing" element={<AdminProtector />}>
          <Route index element={<InvoicesPage />} />
        </Route>

        {/* ! settings paths and card anchors are linked from outside this app: update merchantSettingsSections in packages/corso-lib/generateCrewFrontEndUrl.ts when moving or renaming one */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route element={<AdminProtector />}>
            <Route
              index
              element={
                <Navigate
                  to={{
                    pathname: 'theme',
                    search: new URLSearchParams({
                      [queryParamKey.settingsNavigation]: 'true',
                    }).toString(),
                  }}
                  replace
                />
              }
            />

            <Route
              path="order-editing"
              element={<SettingsPage.OrderEditing />}
            />
            <Route path="test-drive" element={<SettingsPage.TestDrive />} />
            <Route path="theme" element={<SettingsPage.Theme />} />
            <Route path="email" element={<SettingsPage.Email />} />
            <Route
              path="integrations"
              element={<SettingsPage.Integrations />}
            />
            <Route path="returns" element={<SettingsPage.Returns />} />
            <Route path="tracking" element={<SettingsPage.Tracking />} />
            <Route path="warranties" element={<SettingsPage.Warranties />} />
            <Route
              path="registrations"
              element={<SettingsPage.Registrations />}
            />
            <Route path="reasons" element={<SettingsPage.Reasons />} />
            <Route
              path="product-groups"
              element={<SettingsPage.ProductGroups />}
            />
            <Route
              path="notifications"
              element={<SettingsPage.Notifications />}
            />
            <Route
              path="custom-fields"
              element={<SettingsPage.CustomFields />}
            />
            <Route path="metafields" element={<SettingsPage.Metafields />} />
            <Route path="tags" element={<SettingsPage.Tags />} />
            <Route
              path="shipping-claims"
              element={<SettingsPage.ShippingClaims />}
            />

            <Route path="shipping-plus">
              <Route index element={<SettingsPage.ShippingPlus />} />
            </Route>

            <Route path="checkout-widget">
              <Route index element={<SettingsPage.CheckoutWidget />} />
            </Route>

            <Route path="checkout-plus">
              <Route index element={<SettingsPage.CheckoutPlus />} />
            </Route>

            <Route
              path="shipping-policies"
              element={<SettingsPage.ShippingPolicies />}
            />

            <Route path="discounts" element={<SettingsPage.Discounts />} />

            <Route path="automations">
              <Route index element={<SettingsPage.Automations />} />
              <Route
                // => POST create - can have partial store rule when loaded; i.e. from template
                path="create"
                element={<SettingsPage.AutomationForm />}
              />

              <Route
                // => POST edit; GET one available through item details in list
                path="edit/:storeRuleId"
                element={<SettingsPage.AutomationForm />}
              />
            </Route>

            <Route path="data-export" element={<AdminProtector />}>
              <Route index element={<SettingsPage.DataExport />} />
              <Route path="create" element={<SettingsPage.DataExportForm />} />
              <Route
                path="edit/:dataExportDefinitionId"
                element={<SettingsPage.DataExportForm />}
              />
            </Route>

            <Route path="users" element={<SettingsPage.Users />} />

            <Route path="developer">
              <Route index element={<SettingsPage.Developer />} />
              <Route
                path="webhooks/:webhookId"
                element={<SettingsPage.WebhookDeliveries />}
              />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>

    <Route element={<AuxiliaryLayout />}>
      <Route
        path="welcome"
        element={<UserWelcome />}
        loader={UserWelcomeLoader}
      />
      <Route path="sign-up" element={<SignUp />} loader={SignUpLoader} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Route>,
);

export default function Router() {
  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
}
